import { getCalendarWeek } from "./stayPlans";
import { isActivePlanWeek } from "./planFixedDays";

/**
 * Quando o pet de matrícula é agendado fora dos dias fixos, o atendente escolhe
 * entre mudar o plano, trocar só naquela semana ou vender uma avulsa. Este
 * helper decide quais vias fazem sentido e quais dias podem ser sacrificados.
 */
export type OffDayOption = "change_plan" | "swap_week" | "ad_hoc";

export interface OffDayPlanDay {
  id: string;
  date: string;
  status: string;
}

export interface OffDayDecision {
  isOffDay: boolean;
  options: OffDayOption[];
  /** Dias da matrícula naquela semana que ainda podem ceder o lugar. */
  swappableDays: OffDayPlanDay[];
  /** Dias fixos do contrato, candidatos a sair numa mudança de plano. */
  replaceableWeekdays: number[];
  /** Plano quinzenal numa semana de folga: só cabe diária extra. */
  isOffWeek: boolean;
}

const SWAPPABLE_STATUSES = new Set(["scheduled"]);

function weekdayOf(date: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) throw new Error("invalid_calendar_date");
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay();
}

export function resolveOffDayDecision({
  targetDate,
  fixedWeekdays,
  planDays,
  weekInterval = 1,
  startsOn,
}: {
  targetDate: string;
  fixedWeekdays: number[];
  planDays: OffDayPlanDay[];
  /** 2 = quinzenal. Precisa de `startsOn` para saber qual semana é de folga. */
  weekInterval?: number;
  startsOn?: string;
}): OffDayDecision {
  const empty: OffDayDecision = { isOffDay: false, options: [], swappableDays: [], replaceableWeekdays: [], isOffWeek: false };
  if (fixedWeekdays.length === 0) return empty;
  // Semana de folga do quinzenal: nenhum dia é do plano, e mudar dias ou trocar
  // na semana não faz sentido — só a diária extra.
  if (weekInterval > 1 && startsOn && !isActivePlanWeek(startsOn, targetDate, weekInterval)) {
    return {
      isOffDay: true,
      options: ["ad_hoc"],
      swappableDays: [],
      replaceableWeekdays: [...fixedWeekdays].sort((a, b) => a - b),
      isOffWeek: true,
    };
  }
  if (fixedWeekdays.includes(weekdayOf(targetDate))) return empty;

  const week = getCalendarWeek(targetDate);
  const swappableDays = planDays.filter(
    (day) => day.date >= week.start && day.date <= week.end && SWAPPABLE_STATUSES.has(day.status),
  );

  const options: OffDayOption[] = ["change_plan"];
  if (swappableDays.length > 0) options.push("swap_week");
  options.push("ad_hoc");

  return {
    isOffDay: true,
    options,
    swappableDays,
    replaceableWeekdays: [...fixedWeekdays].sort((a, b) => a - b),
    isOffWeek: false,
  };
}
